
import React, { useState, useEffect } from 'react';
import './App.css';

import Dashboard from './components/Dashboard';
import ModeSelection from './components/ModeSelection';
import Slideshow from './components/Slideshow';
import LogoutButton from './components/LogoutButton';
import Footer from './components/Footer';

import { useSessionCleanup, endSession } from './hooks/useSessionCleanup';
import { useAutoHideControls } from './hooks/useAutoHideControls';
import { authenticateWithToken, checkSession, clearClientSessionId, getClientSessionId } from './utils/tokenAuth';

import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function App() {
  const { t } = useTranslation();
  const [subscriptionId, setSubscriptionId] = useState('');
  const [scheduleId, setScheduleId] = useState(''); // Selected schedule ID
  const [scheduleName, setScheduleName] = useState(''); // Display name for tab title
  const [withDescription, setWithDescription] = useState(false); // Show description alongside images
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true); // New: control dashboard vs slideshow
  const [selectedMode, setSelectedMode] = useState(''); // 'browser' or 'device' or '' (not selected)
  const [slideshowIntervalOverride, setSlideshowIntervalOverride] = useState(''); // string for input
  const [concurrentSessionError, setConcurrentSessionError] = useState(false);
  const [tokenExpiredError, setTokenExpiredError] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showTestLogin, setShowTestLogin] = useState(false);
  const [testPassword, setTestPassword] = useState('');
  const [testLoginError, setTestLoginError] = useState('');
  const [testLoginSubmitting, setTestLoginSubmitting] = useState(false);

  const buttonsVisible = useAutoHideControls(5000);
  
  useSessionCleanup(subscriptionId, isSubmitted);

  useEffect(() => {
    document.title = t('app.title');
  }, [subscriptionId, t]);

    // End session when tab/window is closed
    useEffect(() => {
      if (!isSubmitted || !subscriptionId) return;

      const handleUnload = (event) => {
        // Use sendBeacon for reliability
        const url = `${API_BASE}/api/session/end`;
        const data = JSON.stringify({ subscriptionId });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      };

      window.addEventListener('unload', handleUnload);
      window.addEventListener('beforeunload', handleUnload);

      return () => {
        window.removeEventListener('unload', handleUnload);
        window.removeEventListener('beforeunload', handleUnload);
      };
    }, [isSubmitted, subscriptionId]);
  useEffect(() => {
    // Token-based authentication only (no legacy fallback)
    const handleTokenAuth = async () => {
      try {
        const tokenAuthenticated = await authenticateWithToken();
        
        if (tokenAuthenticated) {
          // Token was present and valid - use data directly from token auth
          if (tokenAuthenticated.authenticated) {
            setSubscriptionId(tokenAuthenticated.subscriptionId || '');
            setEmail(tokenAuthenticated.email || '');
            setIsSubmitted(true);
            setShowDashboard(true); // Show Dashboard first for subscription selection
          }
        } else {
          // No token in URL, check if we have existing session cookie
          try {
            const sessionData = await checkSession();
            if (sessionData?.authenticated) {
              setSubscriptionId(sessionData.subscriptionId || '');
              setEmail(sessionData.email || '');
              setIsSubmitted(true);
            
              // Check URL params for direct navigation to slideshow
              const urlParams = new URLSearchParams(window.location.search);
              const paramSubscriptionId = urlParams.get('subscriptionId');
              const paramScheduleId = urlParams.get('scheduleId');
              const paramScheduleName = urlParams.get('scheduleName');
              const paramWithDescription = urlParams.get('withDescription');

              if (paramSubscriptionId) {
                // Direct link to specific subscription/schedule - start slideshow
                setSubscriptionId(paramSubscriptionId);
                if (paramScheduleId) {
                  setScheduleId(paramScheduleId);
                }
                if (paramScheduleName) {
                  setScheduleName(decodeURIComponent(paramScheduleName));
                }
                if (paramWithDescription) {
                  setWithDescription(true);
                }
                setShowDashboard(false); // Go directly to slideshow
                // Clean up URL params
                window.history.replaceState({}, '', window.location.pathname);
              } else {
                setShowDashboard(true); // Show Dashboard for subscription selection
              }
            }
          } catch (err) {
            if (err.code === 'SESSION_ALREADY_ACTIVE') {
              // Concurrent session detected - show error
              console.error('⛔ Concurrent session detected:', err.message);
              alert(t('errors.concurrentSession') || err.message);
              // Clear any session data
              setIsSubmitted(false);
              setSubscriptionId('');
              setEmail('');
            } else {
              // Other errors - not authenticated
              console.log('⚠️ Not authenticated - waiting for Wix token');
            }
          }
        }
      } catch (error) {
        // Handle concurrent session error
        if (error.code === 'SESSION_ALREADY_ACTIVE') {
          console.error('⛔ Concurrent session blocked:', error.message);
          setConcurrentSessionError(true);
          // Don't check for existing session - user must close other tabs
          return;
        }
        // Handle token expiration
        if (error.code === 'TOKEN_EXPIRED') {
          console.error('⏰ Token expired:', error.message);
          setTokenExpiredError(true);
          return;
        }
      } finally {
        setAuthChecked(true);
      }
    };

    handleTokenAuth();
  }, [t]);

  function handleError(err) {
    const status = err?.response?.status;
    const errorCode = err?.response?.data?.error;
    const message = err?.response?.data?.message;
    
    if (errorCode === 'CONCURRENT_VIEWING' || status === 409) {
      const msg = message || 'This subscription is already being viewed in another tab. Please close the other tab first.';
      alert(t('errors.concurrentViewing') || msg);
      // Navigate back to dashboard
      setShowDashboard(true);
      return;
    }
    
    if (errorCode === 'NO_SCHEDULE_MAC') {
      const msg = message || 'スケジュールが選択されていないか、スケジュールにMACアドレスが設定されていません。';
      alert(msg);
      // Navigate back to dashboard to select a valid schedule
      setShowDashboard(true);
      return;
    }
    
    if (status === 403) {
      alert(t('app.emailMismatch') || 'The email does not match the subscription owner.');
    } else {
      alert(t('app.fetchFailed') || 'Failed to fetch schedule.');
    }
    setIsSubmitted(false);
  }

  const handleLogout = () => {
    endSession(subscriptionId);
    clearClientSessionId(); // Clear the client session ID
    setIsSubmitted(false);
    setSubscriptionId('');
    setEmail('');
    setSelectedMode(''); // Reset mode selection
  };

  const handleSelectMode = (mode) => {
    setSelectedMode(mode);
  };

  const handleTestLogin = async (e) => {
    e.preventDefault();
    setTestLoginError('');
    setTestLoginSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/test-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Session-Id': getClientSessionId(),
        },
        credentials: 'include',
        body: JSON.stringify({ password: testPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTestLoginError(data.error || 'ログインに失敗しました。');
        return;
      }
      setEmail(data.email || '');
      setIsSubmitted(true);
      setShowDashboard(true);
    } catch (err) {
      setTestLoginError('ログインに失敗しました。');
    } finally {
      setTestLoginSubmitting(false);
    }
  };

  const handleBackToModeSelection = () => {
    setSelectedMode('');
    setShowDashboard(true); // Reset to dashboard on logout
  };

  const handleStartSlideshow = (selectedSubscriptionId, selectedCategory) => {
    // Update subscriptionId, then hide dashboard
    if (selectedSubscriptionId) {
      setSubscriptionId(selectedSubscriptionId);
    }
    if (selectedCategory?.id) {
      console.log(`Starting slideshow for subscription ${selectedSubscriptionId}, category ${selectedCategory.id} (${selectedCategory.name})`);
    }
    setScheduleName(selectedCategory?.name || '');
    // Reset slideshow interval to server-side default for each new session
    setSlideshowIntervalOverride('');
    setShowDashboard(false); // Switch from dashboard to slideshow
    // Note: schedule will auto-fetch via useSchedule when subscriptionId changes
  };

  if (concurrentSessionError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️ {t('app.concurrentSessionTitle')}</h1>
        <p style={{ fontSize: '1.2rem', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto' }}>
          {t('app.concurrentSessionMessage')}
        </p>
        <Footer />
      </div>
    );
  }

  if (tokenExpiredError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏰ {t('app.tokenExpiredTitle')}</h1>
        <p style={{ fontSize: '1.2rem', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto 2rem' }}>
          {t('app.tokenExpiredMessage')}
        </p>
        <p style={{ marginTop: '30px' }}>
          <a 
            href="https://www.calmu.design" 
            style={{ 
              color: '#426559',
              textDecoration: 'none',
              fontSize: '1.1rem',
              padding: '12px 24px',
              border: '2px solid #426559',
              borderRadius: '5px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#426559';
              e.target.style.color = '#fff';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#426559';
            }}
          >
            {t('app.goToSubscriptionPage') || 'サブスクリプション管理ページへ'}
          </a>
        </p>
        <Footer />
      </div>
    );
  }

  if (!isSubmitted) {
    // While the session/token check is still running, show a neutral loading state
    if (!authChecked) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: 'sans-serif'
        }}>
          <h1>CALMU GALLERY</h1>
          <p>読み込み中...</p>
        </div>
      );
    }

    // Not authenticated and no valid token — users must enter via calmu.design
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h1>CALMU GALLERY</h1>
        <p style={{ fontSize: '18px', marginTop: '20px' }}>
          calmu.design にログインしてギャラリーを開いてください。
        </p>
        <p style={{ marginTop: '30px' }}>
          <a
            href="https://www.calmu.design"
            style={{
              fontSize: '18px',
              color: '#fff',
              textDecoration: 'none',
              padding: '12px 32px',
              borderRadius: '5px',
              display: 'inline-block',
              backgroundColor: '#004B49'
            }}
          >
            calmu.design へ
          </a>
        </p>

        <p style={{ marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => setShowTestLogin(v => !v)}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {showTestLogin ? '▼' : '▶'} テスト用ログイン
          </button>
        </p>
        {showTestLogin && (
          <form onSubmit={handleTestLogin} style={{ marginTop: '10px' }}>
            <input
              type="password"
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              placeholder="パスワード"
              style={{ fontSize: '14px', padding: '8px 12px', borderRadius: '5px', border: '1px solid #ccc' }}
            />
            <button
              type="submit"
              disabled={testLoginSubmitting || !testPassword}
              style={{
                marginLeft: '8px',
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '5px',
                border: 'none',
                backgroundColor: '#004B49',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              ログイン
            </button>
            {testLoginError && (
              <p style={{ color: '#c00', fontSize: '13px', marginTop: '8px' }}>{testLoginError}</p>
            )}
          </form>
        )}

        <Footer />
      </div>
    );
  }

  // Show Dashboard first, then slideshow when user chooses browser mode
  if (showDashboard) {
    // If no mode is selected, show mode selection screen
    if (!selectedMode) {
      return (
        <ModeSelection onSelectMode={handleSelectMode} />
      );
    }
    
    // If mode is selected, show Dashboard with the selected mode
    return (
      <Dashboard 
        subscriptionId={subscriptionId}
        email={email}
        mode={selectedMode}
        onStartSlideshow={handleStartSlideshow}
        onLogout={handleLogout}
        onBackToModeSelection={handleBackToModeSelection}
      />
    );
  }

  // Slideshow view (existing functionality)
  return (
    <div className="fullscreen">
      {buttonsVisible && (
        <div className="bottom-right-controls">
          <button onClick={() => setDebugMode(!debugMode)}>
            {debugMode ? '⚙️ 設定: ON' : '⚙️ 設定: OFF'}
          </button>
          <LogoutButton subscriptionId={subscriptionId} onLogout={handleLogout} />
        </div>
      )}

      <Slideshow
        subscriptionId={subscriptionId}
        scheduleId={scheduleId}
        scheduleName={scheduleName}
        email={email}
        onError={handleError}
        slideshowIntervalOverride={slideshowIntervalOverride}
        onSlideshowIntervalChange={setSlideshowIntervalOverride}
        debugMode={debugMode}
        buttonsVisible={buttonsVisible}
        withDescription={withDescription}
      />
    </div>
  );
}

export default App;